import { useInfiniteQuery } from '@tanstack/react-query';
import { FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import ChannelsFilter from '@/components/ChannelsFilter';
import { Controls, ControlsButton } from '@/components/Controls';
import ExportButton from '@/components/ExportButton';
import FullScreenButton from '@/components/FullScreenButton';
import InfiniteLoader from '@/components/InfiniteLoader';
import InfoTooltip from '@/components/InfoTooltip';
import { SectionHeading, SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import SearchBar from '@/components/SearchBar';
import {
  LargeTh,
  SortableTh,
  Table,
  TableContent,
  TableHeader,
  Th,
} from '@/components/Table';
import Text from '@/components/Text';
import TopicsCreationDropdown from '@/components/TopicsCreationDropdown';
import TopicsFilter from '@/components/TopicsFilter';
import ViewOptionsModal from '@/components/ViewOptionsModal';
import useChannelsFilter from '@/hooks/useChannelsFilter';
import useDebounce from '@/hooks/useDebounce';
import useDidUpdate from '@/hooks/useDidUpdate';
import { selectTeamSettings } from '@/selectors';
import {
  CombinedTopicsType,
  PaginatedResponse,
  SortState,
  TopTopicsResource,
} from '@/types/resources';
import { VideoTopicResource } from '@/types/resources';
import { TeamSettings } from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection, TopicsLevel } from '@/utils/enums';
import { RootState } from '@/utils/store';

import TopCombinedTopicsTable from './TopCombinedTopicsTable';
import TopScriptTopicsFilter from './TopScriptTopicsFilter';
import TopScriptTopicsLevelsFilter from './TopScriptTopicsLevelsFilter';
import TopTopicsFilter from './TopTopicsFilter';
import TopTopicsTableRow from './TopTopicsTableRow';

export enum TopTopicsTabs {
  Competitors = 1,
  Owned = 0,
  All = -1,
}

const ExtraLargeTh = styled(Th)`
  min-width: 169px;
`;

export const StyledSectionHeading = styled(SectionHeading)`
  margin: 0;
  flex: 1;
`;

export const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    vertical-align: top;
    &:nth-child(1) {
      width: 216px;
      min-width: 216px;
      max-width: 216px;
      padding-left: 24px;
      background-color: ${({ theme }) => theme.colors.white};
      position: sticky;
      top: 0;
      left: 0;
      z-index: 1;
    }
    &:last-child {
      width: 56px;
      padding-right: 20px;
      position: relative;
    }
  }
`;

const FiltersHolder = styled.div`
  display: flex;
  gap: 8px;
`;

export const SmallSortableTh = styled(SortableTh)`
  min-width: 87px;
`;

export const MediumSortableTh = styled(SortableTh)`
  min-width: 116px;
`;

const DEFAULT_SORT_STATE: SortState = {
  direction: SortDirection.DESC,
  property: 'vph7',
};

interface Props {
  category: string;
}

const TopTopicsTable: FC<Props> = ({ category }) => {
  const optionsDataKey = () => {
    switch (category) {
      case 'script':
        return 'topScriptTopicsTable';
      case 'thumb':
        return 'topThumbTopicsTable';
      case 'title':
        return 'topTitleTopicsTable';
      case 'type':
        return 'topVideoTypesTable';
      default:
        return 'topScriptTopicsTable';
    }
  };

  const columns = [
    {
      title: 'Topic name',
      toggable: false,
      defaultVisibility: true,
    },
    {
      title: 'AI Suggestions',
      toggable: true,
      defaultVisibility: true,
    },
    {
      title: 'MA Rating',
      toggable: true,
      defaultVisibility: true,
    },
    {
      title: 'Total views',
      toggable: true,
      defaultVisibility: true,
    },
    {
      title: 'Videos',
      toggable: true,
      defaultVisibility: true,
    },
    {
      title: 'VPH7',
      toggable: true,
      defaultVisibility: true,
    },
    {
      title: 'VPH30',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'Combined VPH7',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'Combined VPH30',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'Average V7',
      toggable: true,
      defaultVisibility: true,
    },
    {
      title: 'Average V30',
      toggable: true,
      defaultVisibility: true,
    },
    {
      title: 'V7',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'V30',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'V7 Index',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'V30 Index',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'Virality Rate',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'RPM',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'eYTV (Search)',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'e8min (Search)',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'eYTV 4min (Search)',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'e4min (Search)',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'ViewRate (Search)',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'Rec7',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'Rec30',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'ARP7',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'ARP30',
      toggable: true,
      defaultVisibility: false,
    },
    {
      title: 'Trending in Views',
      toggable: true,
      defaultVisibility: true,
    },
    {
      title: 'Note',
      toggable: true,
      defaultVisibility: false,
    },
  ];

  const organisationName = useSelector(
    (rootState: RootState) => rootState.auth.user!.organisation!.name,
  );

  const tableViewOptions = useSelector(
    (rootState: RootState) => rootState.options.tableViews[optionsDataKey()],
  );

  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounce(search, 400);

  const [activeTab, setActiveTab] = useState(TopTopicsTabs.Competitors);

  const renderTabHeading = (tab: TopTopicsTabs) => {
    switch (tab) {
      case TopTopicsTabs.Competitors:
        return 'Competitors';
      case TopTopicsTabs.Owned:
        return organisationName;
      default:
        return 'All';
    }
  };

  const [combinedTopics, setCombinedTopics] = useState<CombinedTopicsType[]>(
    [],
  );

  const [sort, setSort] = useState<SortState>(DEFAULT_SORT_STATE);

  const [combinationsSort, setCombinationsSort] = useState<SortState>({
    direction: SortDirection.DESC,
    property: 'totalViews',
  });

  const { teamId } = useParams();

  const [filterTopics, setFilterTopics] = useState<VideoTopicResource[]>([]);

  const [selectedCombos, setSelectedCombos] = useState<TopicsLevel[]>([]);

  const [filteredChannelsIds] = useChannelsFilter();

  const teamSettings = useSelector((rootState: RootState) =>
    selectTeamSettings(rootState, teamId!),
  );

  const params = {
    'filter[category]': category,
    ...(activeTab !== TopTopicsTabs.All && {
      'filter[competitor]': activeTab,
    }),
    ...(filteredChannelsIds?.length && {
      'filter[channel_id]': filteredChannelsIds.join(','),
    }),
    ...(!!filterTopics.length && {
      'filter[topic_id]': filterTopics
        .map((topic) => `${topic.id}:${topic.level}`)
        .join(','),
    }),
    ...(!!teamSettings?.[
      `top_${category}_topics_min_videos` as keyof TeamSettings
    ] && {
      'filter[minimum_videos]':
        teamSettings[`top_${category}_topics_min_videos` as keyof TeamSettings],
    }),
    ...(!!teamSettings?.[
      'top_script_topics_min_views' as keyof TeamSettings
    ] && {
      'filter[minimum_views]':
        teamSettings[`top_script_topics_min_views` as keyof TeamSettings],
    }),
    ...(!!teamSettings?.[
      'top_script_topics_min_duration' as keyof TeamSettings
    ] && {
      'filter[minimum_duration]':
        teamSettings[`top_script_topics_min_duration` as keyof TeamSettings],
    }),
    ...(!!teamSettings?.[
      'top_script_topics_max_duration' as keyof TeamSettings
    ] && {
      'filter[maximum_duration]':
        teamSettings[`top_script_topics_max_duration` as keyof TeamSettings],
    }),
    ...(!!teamSettings?.['top_script_topics_min_age' as keyof TeamSettings] && {
      'filter[minimum_age]':
        teamSettings[`top_script_topics_min_age` as keyof TeamSettings],
    }),
    ...(!!teamSettings?.[
      'top_script_topics_min_v7_index' as keyof TeamSettings
    ] && {
      'filter[minimum_v7_index]':
        teamSettings[`top_script_topics_min_v7_index` as keyof TeamSettings],
    }),
    ...(!!selectedCombos.length && {
      'filter[levels]': selectedCombos.join(','),
    }),
    ...(sort.direction !== 'DEFAULT' && {
      sort: `${sort.direction === 'DESC' ? '-' : ''}${sort.property}`,
    }),
    ...(!!debouncedSearch &&
      category === 'thumb' && {
        'filter[search]': debouncedSearch,
      }),
  };

  const fetchTopics = ({ pageParam = `/v1/teams/${teamId}/top-topics` }) => {
    return api.get<never, PaginatedResponse<TopTopicsResource>>(pageParam, {
      params,
    });
  };

  const topicsQueryKey = ['top_topics', { category, teamId }];

  const {
    data,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    remove,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<TopTopicsResource>>(
    topicsQueryKey,
    fetchTopics,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  useDidUpdate(() => {
    remove();
    refetch();
  }, [
    filteredChannelsIds,
    activeTab,
    sort,
    filterTopics,
    teamSettings,
    debouncedSearch,
  ]);

  useDidUpdate(() => {
    if (!filterTopics.length) {
      remove();
      refetch();
    }
  }, [selectedCombos]);

  useDidUpdate(() => {
    setSort(DEFAULT_SORT_STATE);
    setActiveTab(TopTopicsTabs.Competitors);
    setCombinedTopics([]);
    refetch();
  }, [category]);

  const fetchCsv = () =>
    api.get<never, string>(`/v1/teams/${teamId}/top-topics/export`, {
      params,
    });

  useEffect(() => {
    if (!filterTopics.length) return setCombinedTopics([]);

    if (category === 'script') {
      if (!selectedCombos.length) {
        return setCombinedTopics([]);
      }
      api
        .post<object, CombinedTopicsType[]>(
          `/v1/teams/${teamId}/top-script-topics`,
          {
            topics: filterTopics.map((topic) => ({
              id: topic.id,
              level: topic.level,
            })),
            levels: selectedCombos.length ? selectedCombos : [],
          },
          {
            params: {
              ...(activeTab !== TopTopicsTabs.All && {
                competitors: activeTab,
              }),
              ...(filteredChannelsIds?.length && {
                'filter[channel_id]': filteredChannelsIds.join(','),
              }),
              ...(combinationsSort.direction !== 'DEFAULT' && {
                sort: `${combinationsSort.direction === 'DESC' ? '-' : ''}${
                  combinationsSort.property
                }`,
              }),
              ...(!!teamSettings?.[
                `top_${category}_topics_min_videos` as keyof TeamSettings
              ] && {
                'filter[minimum_videos]':
                  teamSettings[
                    `top_${category}_topics_min_videos` as keyof TeamSettings
                  ],
              }),
              ...(!!teamSettings?.[
                'top_script_topics_min_views' as keyof TeamSettings
              ] && {
                'filter[minimum_views]':
                  teamSettings[
                    `top_script_topics_min_views` as keyof TeamSettings
                  ],
              }),
              ...(!!teamSettings?.[
                'top_script_topics_min_duration' as keyof TeamSettings
              ] && {
                'filter[minimum_duration]':
                  teamSettings[
                    `top_script_topics_min_duration` as keyof TeamSettings
                  ],
              }),
              ...(!!teamSettings?.[
                'top_script_topics_max_duration' as keyof TeamSettings
              ] && {
                'filter[maximum_duration]':
                  teamSettings[
                    `top_script_topics_max_duration` as keyof TeamSettings
                  ],
              }),
              ...(!!teamSettings?.[
                'top_script_topics_min_age' as keyof TeamSettings
              ] && {
                'filter[minimum_age]':
                  teamSettings[
                    `top_script_topics_min_age` as keyof TeamSettings
                  ],
              }),
              ...(!!teamSettings?.[
                'top_script_topics_min_v7_index' as keyof TeamSettings
              ] && {
                'filter[minimum_v7_index]':
                  teamSettings[
                    `top_script_topics_min_v7_index` as keyof TeamSettings
                  ],
              }),
            },
          },
        )
        .then((combined) => {
          setCombinedTopics(combined);
        });
      return;
    }
    api
      .get<never, CombinedTopicsType[]>(
        `/v1/teams/${teamId}/top-topics/combined`,
        {
          params: {
            category: category,
            ...(activeTab !== TopTopicsTabs.All && {
              competitor: activeTab,
            }),
            ...(filteredChannelsIds?.length && {
              channel_id: filteredChannelsIds.join(','),
            }),
            topics: filterTopics.map((topic) => topic.id).join(','),
            ...(combinationsSort.direction !== SortDirection.DEFAULT && {
              sortKey: combinationsSort.property,
              sortDirection: combinationsSort.direction,
            }),
          },
        },
      )
      .then((combined) => {
        setCombinedTopics(combined);
      });
  }, [
    filterTopics,
    teamId,
    activeTab,
    category,
    filteredChannelsIds,
    combinationsSort,
    selectedCombos,
    teamSettings,
  ]);

  const topics = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<TopTopicsResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  return (
    <>
      <TableHeader>
        <Controls>
          <ControlsButton
            active={activeTab === TopTopicsTabs.Owned}
            onClick={() => setActiveTab(TopTopicsTabs.Owned)}
          >
            {renderTabHeading(TopTopicsTabs.Owned)}
          </ControlsButton>
          <ControlsButton
            active={activeTab === TopTopicsTabs.Competitors}
            onClick={() => setActiveTab(TopTopicsTabs.Competitors)}
          >
            {renderTabHeading(TopTopicsTabs.Competitors)}
          </ControlsButton>
          <ControlsButton
            active={activeTab === TopTopicsTabs.All}
            onClick={() => setActiveTab(TopTopicsTabs.All)}
          >
            {renderTabHeading(TopTopicsTabs.All)}
          </ControlsButton>
        </Controls>
        <FiltersHolder>
          <ExportButton
            fileName={`top-${category}-topics.csv`}
            fetchCsv={fetchCsv}
          />
          <TopicsCreationDropdown />
          {category !== 'script' && (
            <TopicsFilter
              topics={filterTopics}
              setTopics={setFilterTopics}
              category={category}
            />
          )}
          <ViewOptionsModal columns={columns} tableKey={optionsDataKey()} />
          <FullScreenButton />
        </FiltersHolder>
      </TableHeader>
      <ChannelsFilter />
      {category === 'thumb' && (
        <SearchBar
          value={search}
          setValue={setSearch}
          placeholder="Search by topic name"
        />
      )}
      {teamId !== 'all' && category === 'script' && <TopScriptTopicsFilter />}
      {category === 'script' && (
        <TopScriptTopicsLevelsFilter
          topics={filterTopics}
          setTopics={setFilterTopics}
          category={category}
          setSelectedCombos={setSelectedCombos}
        />
      )}
      <TableHeader>
        <StyledSectionHeading>
          {renderTabHeading(activeTab)} videos
        </StyledSectionHeading>
        {teamId !== 'all' && category !== 'script' && (
          <TopTopicsFilter category={category} />
        )}
      </TableHeader>
      <ScrollableTable>
        <StyledTable>
          <thead>
            <tr>
              <SortableTh
                setSort={setSort}
                sort={sort}
                property="title"
                title="Topic name"
              />
              {tableViewOptions['AI Suggestions'] && (
                <Th>
                  AI Suggestions
                  <InfoTooltip
                    content="AI-generated Ideas and Summaries suggestions of particular topic"
                    title="AI Suggestions"
                  />
                </Th>
              )}
              {tableViewOptions['MA Rating'] && (
                <SmallSortableTh
                  title="MA Rating"
                  setSort={setSort}
                  sort={sort}
                  property="maRating"
                >
                  <InfoTooltip
                    content="Technical analysis method from financial trading to forecast future trends"
                    title="MA Rating"
                  />
                </SmallSortableTh>
              )}
              {tableViewOptions['Total views'] && (
                <SmallSortableTh
                  title="Total Views"
                  setSort={setSort}
                  sort={sort}
                  property="totalViews"
                />
              )}
              {tableViewOptions.Videos && (
                <SmallSortableTh
                  title="Videos"
                  setSort={setSort}
                  sort={sort}
                  property="videosCount"
                />
              )}
              {tableViewOptions.VPH7 && (
                <SmallSortableTh
                  setSort={setSort}
                  sort={sort}
                  property="vph7"
                  title="VPH7"
                >
                  <InfoTooltip
                    content="Average number of views per hour in the last 7 days period among videos with selected topic"
                    title="VPH7"
                  />
                </SmallSortableTh>
              )}
              {tableViewOptions.VPH30 && (
                <SmallSortableTh
                  title="VPH30"
                  setSort={setSort}
                  sort={sort}
                  property="vph30"
                >
                  <InfoTooltip
                    content="Average number of views per hour in the last 30 days period among videos with selected topic"
                    title="VPH30"
                  />
                </SmallSortableTh>
              )}
              {tableViewOptions['Combined VPH7'] && (
                <SmallSortableTh
                  setSort={setSort}
                  sort={sort}
                  property="vph7Sum"
                  title="Combined VPH7"
                >
                  <InfoTooltip
                    content="Sum of views per hour in the last 7 days period among videos with selected topic"
                    title="Combined VPH7"
                  />
                </SmallSortableTh>
              )}
              {tableViewOptions['Combined VPH30'] && (
                <SmallSortableTh
                  title="Combined VPH30"
                  setSort={setSort}
                  sort={sort}
                  property="vph30Sum"
                >
                  <InfoTooltip
                    content="Sum of views per hour in the last 30 days period among videos with selected topic"
                    title="Combined VPH30"
                  />
                </SmallSortableTh>
              )}
              {tableViewOptions['Average V7'] && (
                <MediumSortableTh
                  title="Average V7"
                  subtitle="All time"
                  setSort={setSort}
                  sort={sort}
                  property="v7"
                >
                  <InfoTooltip
                    content="Average number of views per first 7 days among videos with selected topic"
                    title="Average V7"
                  />
                </MediumSortableTh>
              )}
              {tableViewOptions['Average V30'] && (
                <MediumSortableTh
                  title="Average V30"
                  subtitle="All time"
                  setSort={setSort}
                  sort={sort}
                  property="v30"
                >
                  <InfoTooltip
                    content="Average number of views per first 30 days among videos with selected topic"
                    title="Average V30"
                  />
                </MediumSortableTh>
              )}
              {tableViewOptions['V7'] && (
                <MediumSortableTh
                  title="V7"
                  subtitle="All time"
                  setSort={setSort}
                  sort={sort}
                  property="v7Sum"
                >
                  <InfoTooltip
                    content="Number of views per first 7 days"
                    title="V7"
                  />
                </MediumSortableTh>
              )}
              {tableViewOptions['V30'] && (
                <MediumSortableTh
                  title="V30"
                  subtitle="All time"
                  setSort={setSort}
                  sort={sort}
                  property="v30Sum"
                >
                  <InfoTooltip
                    content="Number of views per first 30 days"
                    title="V30"
                  />
                </MediumSortableTh>
              )}
              {tableViewOptions['V7 Index'] && (
                <MediumSortableTh
                  title="V7 Index"
                  setSort={setSort}
                  sort={sort}
                  property="v7Index"
                >
                  <InfoTooltip
                    content="Average V7 among videos with selected topic compared to channel's average V7 of the last 90 days"
                    title="V7 Index"
                  />
                </MediumSortableTh>
              )}
              {tableViewOptions['V30 Index'] && (
                <MediumSortableTh
                  title="V30 Index"
                  setSort={setSort}
                  sort={sort}
                  property="v30Index"
                >
                  <InfoTooltip
                    content="Average V30 among videos with selected topic compared to channel's average V30 of the last 90 days"
                    title="V30 Index"
                  />
                </MediumSortableTh>
              )}
              {tableViewOptions['Virality Rate'] && (
                <MediumSortableTh
                  title="Virality Rate"
                  setSort={setSort}
                  sort={sort}
                  property="viralityRate"
                >
                  <InfoTooltip
                    content="Probability for topic to go viral. Ratio between total number of videos with selected topic and videos where v7_index > 2"
                    title="Virality Rate"
                  />
                </MediumSortableTh>
              )}
              {tableViewOptions.RPM && (
                <SortableTh
                  property="rpm"
                  title="RPM"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip
                    content="Average Revenue per 1000 views among videos with selected topic"
                    title="RPM"
                  />
                </SortableTh>
              )}
              {tableViewOptions['eYTV (Search)'] && (
                <SortableTh
                  property="ga_search_eytv"
                  title="eYTV (Search)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="eYTV (Search)" />
                </SortableTh>
              )}
              {tableViewOptions['e8min (Search)'] && (
                <SortableTh
                  property="ga_search_views_percentage"
                  title="e8min (Search)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="e8min (Search)" />
                </SortableTh>
              )}
              {tableViewOptions['eYTV 4min (Search)'] && (
                <SortableTh
                  property="ga_search_eytv_4"
                  title="eYTV 4min (Search)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="eYTV 4min (Search)" />
                </SortableTh>
              )}
              {tableViewOptions['e4min (Search)'] && (
                <SortableTh
                  property="ga_search_e4min"
                  title="e4min (Search)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="e4min (Search)" />
                </SortableTh>
              )}
              {tableViewOptions['ViewRate (Search)'] && (
                <SortableTh
                  property="ga_search_view_rate_percentage"
                  title="View Rate (Search)"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="View Rate (Search)" />
                </SortableTh>
              )}
              {tableViewOptions['Rec7'] && (
                <SortableTh
                  property="yt_rec_7"
                  title="Rec7"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="Rec7" />
                </SortableTh>
              )}
              {tableViewOptions['Rec30'] && (
                <SortableTh
                  property="yt_rec_30"
                  title="Rec30"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="Rec30" />
                </SortableTh>
              )}
              {tableViewOptions['ARP7'] && (
                <SortableTh
                  property="yt_avg_position_7"
                  title="ARP7"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="ARP7" />
                </SortableTh>
              )}
              {tableViewOptions['ARP30'] && (
                <SortableTh
                  property="yt_avg_position_30"
                  title="ARP30"
                  setSort={setSort}
                  sort={sort}
                >
                  <InfoTooltip content="-" title="ARP30" />
                </SortableTh>
              )}
              {tableViewOptions['Trending in Views'] && (
                <LargeTh>
                  Trending in Views
                  <InfoTooltip
                    content="Total number of views of videos with selected topic; graph shows 14 weeks and compares first 7 weeks with last 7 weeks"
                    title="Trending in Views (Weekly)"
                  />
                  <br />
                  <Text as="span" $color="gray" $type="extraSmall">
                    Weekly
                  </Text>
                </LargeTh>
              )}
              {tableViewOptions.Note && <ExtraLargeTh>Note</ExtraLargeTh>}
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              isError={isError}
              hasData={!!topics?.length}
            >
              {topics?.map((topic) => (
                <TopTopicsTableRow
                  optionsKey={optionsDataKey()}
                  key={topic.id}
                  topic={topic}
                  category={category}
                  level={filterTopics.find((f) => f.id === topic.id)?.level}
                  topicsQueryKey={topicsQueryKey}
                  activeTab={activeTab}
                  filteredChannelsIds={filteredChannelsIds}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ScrollableTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
      {!!combinedTopics.length && (
        <TopCombinedTopicsTable
          optionsKey={optionsDataKey()}
          combinedTopics={combinedTopics}
          activeTab={activeTab}
          sort={combinationsSort}
          setSort={setCombinationsSort}
          filteredChannelsIds={filteredChannelsIds}
        />
      )}
    </>
  );
};

export default TopTopicsTable;
